import { useState } from "react";
import { useAuth } from "@clerk/react";
import { HugeiconsIcon } from "@hugeicons/react";
import {
  Tick02Icon,
  ArrowRight01Icon,
  Crown02Icon,
  Calendar03Icon,
  CreditCardIcon,
  Invoice01Icon,
} from "@hugeicons/core-free-icons";
import {
  Card,
  CardHeader,
  CardTitle,
  CardDescription,
  CardContent,
} from "@/components/ui/card";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Separator } from "@/components/ui/separator";
import { Switch } from "@/components/ui/switch";
import { AnimatedShinyText } from "@/components/ui/animated-shiny-text";
import { plans, enterpriseFeatures } from "@/components/pricing/PricingCards";
import type { Plan } from "@/components/pricing/PricingCards";
import { createCreditsApi } from "@/lib/api/credits";
import type { BalanceResponse } from "@/lib/api/credits";
import { useCreditsBalance } from "@/lib/hooks/use-credits";
import { Skeleton } from "@/components/ui/skeleton";
import { cn } from "@/lib/utils";
import logoLight from "@/assets/png/logo.png";
import logoDark from "@/assets/png/logo_reversed.png";

/* ────────────────── Manage Plan Dialog ─────────────────────────── */

function ManagePlanDialog({
  open,
  onOpenChange,
  planName,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  planName: string;
}) {
  const { getToken } = useAuth();
  const [isRedirecting, setIsRedirecting] = useState(false);

  async function openPortal() {
    setIsRedirecting(true);
    try {
      const api = createCreditsApi({ getToken });
      const { url } = await api.portal();
      window.location.assign(url);
    } catch {
      setIsRedirecting(false);
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Manage plan</DialogTitle>
          <DialogDescription>Subscription & billing settings</DialogDescription>
        </DialogHeader>

        <Separator className="-mx-4 w-[calc(100%+2rem)]" />

        {/* Current plan card */}
        <div className="bg-muted dark:bg-white/6 flex items-center gap-3 rounded-lg p-4">
          <img
            src={logoLight}
            alt="Rayloop"
            className="h-10 w-10 rounded-lg object-contain dark:hidden"
          />
          <img
            src={logoDark}
            alt="Rayloop"
            className="hidden h-10 w-10 rounded-lg object-contain dark:block"
          />
          <div>
            <p className="text-sm font-semibold">You're on {planName} Plan</p>
            <p className="text-muted-foreground text-xs">Upgrade anytime</p>
          </div>
        </div>

        {/* Coupon code — hidden until Stripe promo code API is integrated */}
        {/* <div className="flex items-start gap-2">
          <HugeiconsIcon
            icon={InformationCircleIcon}
            className="text-muted-foreground mt-0.5 size-4 shrink-0"
          />
          <div className="flex flex-1 flex-col gap-2">
            <span className="text-sm">Have a coupon code?</span>
            <div className="flex gap-2">
              <Input
                placeholder="Enter code"
                value={coupon}
                onChange={(e) => setCoupon(e.target.value)}
                className="h-8 text-sm"
              />
              <Button variant="outline" size="sm" disabled={!coupon.trim()}>
                Apply
              </Button>
            </div>
          </div>
        </div> */}

        {/* Footer actions — both open Stripe Customer Portal */}
        <DialogFooter className="sm:justify-stretch">
          <Button
            variant="outline"
            className="flex-1 gap-2"
            disabled={isRedirecting}
            onClick={openPortal}
          >
            <HugeiconsIcon icon={CreditCardIcon} className="size-4" />
            Edit billing information
          </Button>
          <Button
            variant="outline"
            className="flex-1 gap-2"
            disabled={isRedirecting}
            onClick={openPortal}
          >
            <HugeiconsIcon icon={Invoice01Icon} className="size-4" />
            Invoices & payments
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

/* ────────────────── Current Plan Banner Skeleton ───────────────── */

function CurrentPlanBannerSkeleton() {
  return (
    <Card className="bg-transparent">
      <CardContent>
        <div className="grid gap-6 sm:grid-cols-2">
          {/* Left — Plan info */}
          <div className="flex flex-col gap-3">
            <div className="flex items-center gap-2">
              <Skeleton className="size-5 rounded" />
              <Skeleton className="h-5 w-24 rounded" />
              <Skeleton className="h-5 w-12 rounded-full" />
            </div>
            <Skeleton className="h-4 w-56 rounded" />
            <div className="mt-1">
              <Skeleton className="h-8 w-28 rounded-md" />
            </div>
          </div>

          {/* Right — Credit usage */}
          <div className="flex flex-col gap-3">
            <div className="flex items-center justify-between">
              <Skeleton className="h-4 w-24 rounded" />
              <Skeleton className="h-4 w-16 rounded" />
            </div>
            <Skeleton className="h-2.5 w-full rounded-full" />
            <div className="flex items-center justify-between">
              <Skeleton className="h-3 w-28 rounded" />
              <Skeleton className="h-3 w-16 rounded" />
            </div>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

/* ────────────────── Current Plan Banner ────────────────────────── */

function CurrentPlanBanner({ balance }: { balance: BalanceResponse }) {
  const [managePlanOpen, setManagePlanOpen] = useState(false);

  const planIndex = ["free", "pro", "max"].indexOf(balance.plan);
  const plan = plans[planIndex] ?? plans[0];
  const planName = plan.name;

  const resetLabel = balance.credits_reset_at
    ? new Date(balance.credits_reset_at).toLocaleDateString(undefined, {
        month: "short",
        day: "numeric",
      })
    : "—";

  return (
    <>
      <ManagePlanDialog
        open={managePlanOpen}
        onOpenChange={setManagePlanOpen}
        planName={planName}
      />
      <Card className="bg-transparent">
        <CardContent>
          <div className="grid gap-6 sm:grid-cols-2">
            {/* Left — Plan info */}
            <div className="flex flex-col gap-3">
              <div className="flex items-center gap-2">
                <HugeiconsIcon
                  icon={Crown02Icon}
                  className="text-brand size-5"
                />
                <span className="text-base font-semibold">{planName} plan</span>
                <Badge variant="secondary" className="text-xs">
                  Active
                </Badge>
              </div>
              <p className="text-muted-foreground text-sm">
                {plan.description}
              </p>
              <div className="mt-1">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setManagePlanOpen(true)}
                >
                  Manage plan
                </Button>
              </div>
            </div>

            {/* Right — Credit usage */}
            <div className="flex flex-col gap-3">
              <div className="flex items-center justify-between">
                <span className="text-sm font-semibold">Credits remaining</span>
                <span className="text-sm font-semibold tabular-nums">
                  {balance.total.toLocaleString()}
                </span>
              </div>

              {/* Progress bar — shows combined remaining vs monthly allocation */}
              <div className="bg-muted h-2.5 w-full overflow-hidden rounded-full">
                <div
                  className="h-full rounded-full transition-all bg-gradient-to-r from-[oklch(0.72_0.22_30)] via-[oklch(0.68_0.22_350)] to-[oklch(0.62_0.22_285)] dark:from-[oklch(0.62_0.25_30)] dark:via-[oklch(0.58_0.25_350)] dark:to-[oklch(0.52_0.25_285)]"
                  style={{ width: `${Math.min(100, Math.round((balance.total / balance.plan_credits_monthly) * 100))}%` }}
                />
              </div>

              {/* Credit breakdown rows */}
              <div className="flex flex-col gap-1.5">
                <div className="flex items-center justify-between">
                  <div>
                    <span className="text-xs font-medium">Monthly credits</span>
                    <p className="text-muted-foreground text-xs">Resets {resetLabel}</p>
                  </div>
                  <span className="text-xs font-medium tabular-nums">
                    {balance.plan_credits.toLocaleString()}
                  </span>
                </div>

                {balance.purchased_credits > 0 && (
                  <div className="flex items-center justify-between">
                    <div>
                      <span className="text-xs font-medium">Extra credits</span>
                      <p className="text-muted-foreground text-xs">
                        {balance.purchased_credits_expires_at
                          ? `Expires ${new Date(balance.purchased_credits_expires_at).toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" })}`
                          : "Never expire"}
                      </p>
                    </div>
                    <span className="text-xs font-medium tabular-nums">
                      {balance.purchased_credits.toLocaleString()}
                    </span>
                  </div>
                )}
              </div>
            </div>
          </div>
        </CardContent>
      </Card>
    </>
  );
}

/* ────────────────── Plan Comparison Card ────────────────────────── */

function PlanCard({
  plan,
  isCurrent,
  isAnnual,
  onToggleAnnual,
  onUpgrade,
  isUpgrading,
}: {
  plan: Plan;
  isCurrent: boolean;
  isAnnual: boolean;
  onToggleAnnual: (val: boolean) => void;
  onUpgrade?: () => void;
  isUpgrading?: boolean;
}) {
  const price = isAnnual ? plan.annualPrice : plan.monthlyPrice;
  const annualSavings = (plan.monthlyPrice - plan.annualPrice) * 12;

  return (
    <Card
      className={cn(
        "bg-transparent flex flex-col",
        plan.highlighted && "ring-brand/40 ring-1",
      )}
    >
      <CardHeader>
        <div className="flex items-center justify-between">
          <CardTitle>{plan.name}</CardTitle>
          {plan.highlighted && (
            <Badge className="bg-brand/15 border-brand/30 border px-3 py-1">
              <AnimatedShinyText className="text-brand text-xs font-semibold">
                Most Popular
              </AnimatedShinyText>
            </Badge>
          )}
        </div>
        <CardDescription>{plan.description}</CardDescription>
      </CardHeader>

      <CardContent className="flex flex-1 flex-col gap-5">
        {/* Price */}
        <div>
          <div className="flex items-baseline gap-1">
            <span className="text-3xl font-semibold tracking-tight">
              ${price}
            </span>
            <span className="text-muted-foreground text-sm">/month</span>
          </div>
          <p className="text-brand mt-1 text-sm font-medium">{plan.credits}</p>
        </div>

        {/* Annual toggle inside card */}
        {plan.monthlyPrice > 0 ? (
          <>
            <Separator />
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2.5">
                <Switch
                  checked={isAnnual}
                  onCheckedChange={onToggleAnnual}
                  className="h-5 w-9 **:data-[slot=switch-thumb]:size-4 **:data-[slot=switch-thumb]:data-[state=checked]:translate-x-4"
                />
                <span className="text-sm font-medium">Annual</span>
              </div>
              {isAnnual && annualSavings > 0 && (
                <Badge
                  variant="outline"
                  className="border-emerald-500/30 bg-emerald-500/10 px-3 py-1"
                >
                  <AnimatedShinyText className="text-emerald-500 text-xs font-semibold">
                    Save ${annualSavings}
                  </AnimatedShinyText>
                </Badge>
              )}
            </div>
            <Separator />
          </>
        ) : (
          <>
            <Separator />
            <p className="text-muted-foreground text-sm">Free forever</p>
            <Separator />
          </>
        )}

        {/* CTA */}
        <Button
          variant={
            isCurrent ? "outline" : plan.highlighted ? "default" : "outline"
          }
          className={cn(
            "w-full gap-2",
            plan.highlighted &&
              !isCurrent &&
              "bg-brand text-brand-foreground hover:bg-brand/90 border-none",
          )}
          disabled={isCurrent || isUpgrading || !onUpgrade}
          onClick={onUpgrade}
        >
          {isUpgrading ? "Redirecting…" : isCurrent ? "Current plan" : plan.cta}
          {!isCurrent && !isUpgrading && onUpgrade && (
            <HugeiconsIcon
              icon={ArrowRight01Icon}
              className="size-4"
              data-icon="inline-end"
            />
          )}
        </Button>

        {/* Features */}
        <ul className="space-y-2.5">
          {plan.features.map((feature) => (
            <li key={feature.text} className="flex items-start gap-2.5 text-sm">
              <HugeiconsIcon
                icon={Tick02Icon}
                className={cn(
                  "mt-0.5 size-4 shrink-0",
                  plan.highlighted ? "text-brand" : "text-muted-foreground",
                )}
              />
              <span className="text-foreground/80">
                {feature.text}
                {feature.comingSoon && (
                  <span className="text-muted-foreground/50"> *</span>
                )}
              </span>
            </li>
          ))}
        </ul>
      </CardContent>
    </Card>
  );
}

/* ────────────────── Enterprise Card ─────────────────────────────── */

function EnterpriseCard() {
  return (
    <Card className="bg-transparent">
      <CardHeader>
        <CardTitle>Enterprise</CardTitle>
        <CardDescription>
          Built for large organisations needing flexibility, scale, and
          governance.
        </CardDescription>
      </CardHeader>

      <CardContent className="flex flex-col gap-5">
        {/* Price + CTA */}
        <div className="flex items-center justify-between">
          <div>
            <span className="text-3xl font-semibold tracking-tight">
              Custom
            </span>
            <p className="text-muted-foreground mt-1 text-sm">
              Flexible plans tailored to your needs
            </p>
          </div>
          <Button variant="outline" className="gap-2">
            <HugeiconsIcon
              icon={Calendar03Icon}
              className="size-4"
              data-icon="inline-start"
            />
            Book a demo
          </Button>
        </div>

        <Separator />

        {/* Features — 3 columns */}
        <div className="grid grid-cols-2 gap-x-8 gap-y-2.5 sm:grid-cols-3">
          {enterpriseFeatures.map((f) => (
            <div key={f} className="flex items-center gap-2 text-sm">
              <HugeiconsIcon
                icon={Tick02Icon}
                className="text-muted-foreground size-3.5 shrink-0"
              />
              <span className="text-foreground/80">{f}</span>
            </div>
          ))}
        </div>
      </CardContent>
    </Card>
  );
}

/* ────────────────── Main Export ─────────────────────────────────── */

export function PlansUsageSettings() {
  const { getToken } = useAuth();
  const { data: balance } = useCreditsBalance();
  const [isAnnual, setIsAnnual] = useState(true);
  const [upgradingPlan, setUpgradingPlan] = useState<string | null>(null);

  const currentPlan = balance?.plan ?? "free";

  async function handleUpgrade(planSlug: "pro" | "max") {
    setUpgradingPlan(planSlug);
    try {
      const api = createCreditsApi({ getToken });
      const { url } = await api.subscribe(
        planSlug,
        isAnnual ? "annual" : "monthly",
      );
      window.location.assign(url);
    } catch {
      setUpgradingPlan(null);
    }
  }

  return (
    <div className="w-full space-y-6 p-8">
      <div>
        <h1 className="text-xl font-semibold">Plans & usage</h1>
        <p className="text-muted-foreground text-sm">
          Manage your subscription plan and credit balance.
        </p>
      </div>
      <Separator />
      {balance ? <CurrentPlanBanner balance={balance} /> : <CurrentPlanBannerSkeleton />}
      <div className="grid gap-4 sm:grid-cols-3">
        {plans.map((plan) => {
          const planSlug = plan.name.toLowerCase() as "free" | "pro" | "max";
          const isCurrent = planSlug === currentPlan;
          const canUpgrade = !isCurrent && planSlug !== "free";

          return (
            <PlanCard
              key={plan.name}
              plan={plan}
              isCurrent={isCurrent}
              isAnnual={isAnnual}
              onToggleAnnual={setIsAnnual}
              onUpgrade={
                canUpgrade
                  ? () => handleUpgrade(planSlug as "pro" | "max")
                  : undefined
              }
              isUpgrading={upgradingPlan === planSlug}
            />
          );
        })}
      </div>
      <EnterpriseCard />
    </div>
  );
}
